import * as THREE from "three";
import { CameraCore } from "../cameraCore";
import { hqNode } from "./common";

/**
 * The fortress pennant turned to face the game camera (in-game check 2026-10-22: from the default battle camera it flew
 * edge-on, so the worn emblem — flagEmblem.ts, on both faces of the cloth — could not be read).
 *
 * Every fortress GLB (`base_t<n>`, its skins) has a `pennant` node whose idle clip swings the cloth a few degrees each
 * way about the pole. The cloth's facing is measured from its own vertex normals (clothNormal). A pivot group is slipped in between the node and
 * its parent at the node's own origin (the pole), carrying one fixed yaw: the clips keep driving the node as before
 * (rigid rigs skin by each node's world matrix, rigidRig.ts), and the swing now happens round a cloth that faces the
 * camera's diagonal. The cloth has two faces with the emblem reading right on both, so the turn is the smaller of the
 * two that make it face the camera (|yaw| ≤ 90°).
 */
export class PennantFacing {
  static readonly Part = "pennant";
  static readonly PivotName = "pennantFacing";
  /** The cloth's material (every fortress model's `<model>_team`, dressed by flagEmblem.ts). */
  static readonly Cloth = /_team$/;

  /** World yaw (rotation.y convention: 0 = +z) of the direction from a model toward the level camera at its default turn. */
  static cameraYaw(): number {
    const yaw = THREE.MathUtils.degToRad(CameraCore.DefaultYaw);
    return Math.atan2(-Math.sin(yaw), -Math.cos(yaw));
  }

  /** The mean turn (rad about +Y) of node `node` over the idle clip, from its quaternion track; 0 without one. */
  static idleSwing(clips: readonly THREE.AnimationClip[], node: string): number {
    const track = clips.find((c) => c.name === "idle")?.tracks.find((t) => t.name === `${node}.quaternion`);
    if (!track || track.values.length < 4) return 0;
    let sum = 0;
    const n = track.values.length / 4;
    for (let i = 0; i < n; i++) sum += 2 * Math.atan2(track.values[i * 4 + 1], track.values[i * 4 + 3]);
    return sum / n;
  }

  /** The turn that makes a cloth facing world yaw `cloth` face world yaw `toward`, either face: in (−π/2, π/2]. */
  static turnFor(cloth: number, toward: number): number {
    let d = toward - cloth;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    if (d > Math.PI / 2) d -= Math.PI;
    if (d <= -Math.PI / 2) d += Math.PI;
    return d;
  }

  /**
   * The cloth's mean normal in the pennant node's own frame (its rotation left out): the vertex normals of the cloth
   * under it (the `_team` material, flagEmblem.ts), each sheet's sign folded onto the first (the cloth is two sheets,
   * ±n). +z when it has none.
   */
  static clothNormal(node: THREE.Object3D): THREE.Vector3 {
    const sum = new THREE.Vector3(), n = new THREE.Vector3(), first = new THREE.Vector3();
    const toNode = new THREE.Matrix4().copy(node.matrixWorld).invert();
    const m = new THREE.Matrix4(), basis = new THREE.Matrix3();
    node.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || !([] as THREE.Material[]).concat(mesh.material).some((mt) => PennantFacing.Cloth.test(mt.name))) return;
      const normals = mesh.geometry.getAttribute("normal");
      if (!normals) return;
      basis.getNormalMatrix(m.multiplyMatrices(toNode, o.matrixWorld));
      for (let i = 0; i < normals.count; i++) {
        n.fromBufferAttribute(normals, i).applyMatrix3(basis).normalize();
        if (first.lengthSq() === 0) first.copy(n);
        sum.addScaledVector(n, n.dot(first) < 0 ? -1 : 1);
      }
    });
    sum.y = 0;
    return sum.lengthSq() > 1e-12 ? sum.normalize() : new THREE.Vector3(0, 0, 1);
  }

  /**
   * Turns the pennant of fortress model `model` (named `hq`) inside `storeys`, which stands at world yaw `storeysYaw`,
   * to face the camera; `clips` are the model's (the idle swing is read from them the first time). Call again after the
   * fortress turns (a level's heading): it re-aims the same pivot.
   */
  static face(storeys: THREE.Object3D, hq: string, storeysYaw: number, clips: readonly THREE.AnimationClip[] = []): void {
    const node = hqNode(storeys, hq, PennantFacing.Part);
    if (!node || !node.parent) return;
    let pivot = node.parent;
    if (pivot.name !== PennantFacing.PivotName) {
      const parent = node.parent;
      pivot = new THREE.Group();
      pivot.name = PennantFacing.PivotName;
      pivot.position.copy(node.position);
      parent.add(pivot);
      pivot.add(node);
      node.position.set(0, 0, 0);
      pivot.userData.swing = PennantFacing.idleSwing(clips, node.name);
      storeys.updateMatrixWorld(true);
      pivot.userData.normal = PennantFacing.clothNormal(node);
    }
    // the cloth's normal in the node's frame, turned by the idle swing's mean, then into the storeys' frame
    pivot.rotation.set(0, 0, 0);
    storeys.updateMatrixWorld(true);
    const toStoreys = new THREE.Quaternion().copy(storeys.getWorldQuaternion(new THREE.Quaternion())).invert()
      .multiply(pivot.getWorldQuaternion(new THREE.Quaternion()));
    const normal = (pivot.userData.normal as THREE.Vector3).clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), pivot.userData.swing as number).applyQuaternion(toStoreys);
    const cloth = storeysYaw + Math.atan2(normal.x, normal.z);
    pivot.rotation.y = PennantFacing.turnFor(cloth, PennantFacing.cameraYaw());
    storeys.updateMatrixWorld(true);
  }
}
